import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import AppHeader, { defaultUserAvatar } from '../components/layout/AppHeader';
import { resendOtp, verifyOtp } from '../services/authService';
import { showAppError } from '../services/messageDialogService';
import { showAuthError } from '../utils/showAuthError';
import { getAuthPhoneDetails } from '../storage/authStorage';
import { BAZZAR_PATH } from '../routes/paths';

function formatPhoneDisplay(phone, countryCode) {
  if (!phone) return '';
  return `+${countryCode} ${phone}`;
}

function getIdentifierDisplay({ method, phone, email, countryCode }) {
  if (method === 'email') return email || '';
  return formatPhoneDisplay(phone, countryCode);
}

export default function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);

  const phoneDetails = useMemo(() => {
    const fromState = location.state || {};
    const fromStorage = getAuthPhoneDetails();

    return {
      phone: fromState.phone || fromStorage.phone,
      email: fromState.email || fromStorage.email,
      method: fromState.method || fromStorage.method || 'phone',
      identifier: fromState.identifier || fromStorage.identifier,
      countryCode: fromState.countryCode || fromStorage.countryCode,
      countryIso: fromState.countryIso || fromStorage.countryIso,
    };
  }, [location.state]);

  useEffect(() => {
    if (!phoneDetails.identifier && !phoneDetails.phone && !phoneDetails.email) {
      navigate('/', { replace: true });
    }
  }, [navigate, phoneDetails.email, phoneDetails.identifier, phoneDetails.phone]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const applyOtpDigits = (startIndex, digits) => {
    if (!digits) return;

    const next = [...otp];
    let lastFilledIndex = startIndex;

    digits
      .slice(0, otp.length - startIndex)
      .split('')
      .forEach((digit, offset) => {
        const targetIndex = startIndex + offset;
        next[targetIndex] = digit;
        lastFilledIndex = targetIndex;
      });

    setOtp(next);

    if (lastFilledIndex < otp.length - 1) {
      inputRefs.current[lastFilledIndex + 1]?.focus();
    } else {
      inputRefs.current[otp.length - 1]?.focus();
    }
  };

  const handleOtpChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      const next = [...otp];
      next[index] = '';
      setOtp(next);
      return;
    }

    // Handles browser OTP autofill and pasted multi-digit values.
    if (digits.length > 1) {
      applyOtpDigits(index, digits);
      return;
    }

    const digit = digits.slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);

    if (digit && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (index, event) => {
    event.preventDefault();
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedDigits) return;
    applyOtpDigits(index, pastedDigits);
  };

  const handleVerify = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      showAppError('Please enter the 6-digit OTP.');
      return;
    }

    setLoading(true);

    try {
      await verifyOtp({
        method: phoneDetails.method,
        phone: phoneDetails.phone,
        email: phoneDetails.email,
        countryCode: phoneDetails.countryCode,
        countryIso: phoneDetails.countryIso,
        otp: otpValue,
      });

      navigate(BAZZAR_PATH, { replace: true });
    } catch (error) {
      showAuthError(error, 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resending || resendCooldown > 0) return;
    setResending(true);

    try {
      await resendOtp({
        method: phoneDetails.method,
        phone: phoneDetails.phone,
        email: phoneDetails.email,
        countryCode: phoneDetails.countryCode,
        countryIso: phoneDetails.countryIso,
      });
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setResendCooldown(30);
      toast.success('OTP sent again.');
    } catch (error) {
      showAuthError(error, 'Unable to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <MainLayout>
      <AppHeader showSubtitle endVariant="profile" avatarSrc={defaultUserAvatar} />
      <Box
        sx={{
          minHeight: { xs: '68vh', md: '72vh' },
          display: 'grid',
          placeItems: 'center',
          py: { xs: 2, md: 5 },
          px: { xs: 1.25, sm: 2 },
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            width: '100%',
            maxWidth: { xs: '100%', sm: 480 },
            minHeight: { xs: 'auto', sm: '443px' },
            pt: { xs: 3, sm: '48px' },
            px: { xs: 1.5, sm: 3 },
            pb: 3,
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2.5, sm: '38px' },
            borderRadius: { xs: '24px', sm: '36px' },
            bgcolor: 'rgba(0, 0, 0, 0.50)',
            boxSizing: 'border-box',
          }}
        >
          <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ width: '100%', maxWidth: 400 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0.75, sm: 1.2 },
                flexWrap: 'wrap',
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <IconButton
                component={RouterLink}
                to="/"
                size="small"
                sx={{
                  width: 30,
                  height: 30,
                  p: 0,
                  flexShrink: 0,
                  background: 'linear-gradient(180deg, #5A04D7 0%, #FF00F2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(180deg, #5A04D7 0%, #FF00F2 100%)',
                  },
                }}
              >
                <ArrowBackIcon sx={{ color: '#fff', fontSize: 17 }} />
              </IconButton>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#fff',
                  fontSize: { xs: 20, sm: 24, md: 28 },
                  letterSpacing: { xs: '1px', sm: '2.5px' },
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                VERIFY YOUR OTP
              </Typography>
            </Box>

            <Typography
              sx={{
                color: '#fff',
                textAlign: 'center',
                fontSize: { xs: 14, sm: 16 },
                letterSpacing: '0.8px',
                px: 0.5,
                wordBreak: 'break-word',
              }}
            >
              Please enter OTP sent on{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {getIdentifierDisplay(phoneDetails)}
              </Box>
            </Typography>

            <Box
              sx={{
                width: '100%',
                maxWidth: 360,
                mx: 'auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                gap: { xs: 1, sm: 1.5, md: 2.5 },
              }}
            >
              {otp.map((digit, index) => (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    maxWidth: 48,
                    maxHeight: 48,
                    mx: 'auto',
                    borderRadius: '100px',
                    border: '1px solid #D43CCA',
                    bgcolor: 'rgba(0, 0, 0, 0.66)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Box
                    component="input"
                    ref={(element) => {
                      inputRefs.current[index] = element;
                    }}
                    value={digit}
                    onChange={(event) => handleOtpChange(index, event.target.value)}
                    onKeyDown={(event) => handleOtpKeyDown(index, event)}
                    onPaste={(event) => handleOtpPaste(index, event)}
                    maxLength={1}
                    inputMode="numeric"
                    sx={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      color: '#E6D4FF',
                      textAlign: 'center',
                      fontSize: 16,
                      fontWeight: 600,
                      letterSpacing: '0.8px',
                    }}
                  />
                </Box>
              ))}
            </Box>

            <Typography
              onClick={resending || resendCooldown > 0 ? undefined : handleResend}
              sx={{
                color: '#FF00F2',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: { xs: 14, sm: 18 },
                textTransform: 'uppercase',
                cursor: resending || resendCooldown > 0 ? 'default' : 'pointer',
                opacity: resending || resendCooldown > 0 ? 0.6 : 1,
                px: 0.5,
                lineHeight: 1.3,
              }}
            >
              {resending ? 'Sending...' : resendCooldown > 0 ? `RESEND OTP IN ${resendCooldown}s` : 'RESEND OTP'}
            </Typography>

            <Button
              onClick={handleVerify}
              disabled={loading}
              sx={{
                mt: 0.5,
                height: 50,
                width: '100%',
                borderRadius: '100px',
                border: '1px solid #FFF',
                color: '#fff',
                background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
                boxShadow: '0 0 20px rgba(252, 0, 241, 0.36)',
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 500,
                textTransform: 'uppercase',
                '&:hover': {
                  background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
                },
                '&.Mui-disabled': {
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'PROCEED'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </MainLayout>
  );
}
